import type { Assessment, MarkSet, MarkSetCategory } from '../types';

/**
 * Moving a class from one grading framework to another.
 *
 * Swapping `ClassData.markSetId` on its own LOOKS like it works and silently
 * destroys every mark in the class. `calculateAverage` walks the new mark set's
 * categories and collects assessments by `a.categoryId === category.id`; after
 * a bare swap no assessment matches any category, so `totalWeight` stays 0 and
 * the function returns null — every student's average simply disappears, with
 * no error and all the grades still sitting in the document.
 *
 * So a change of mark set is a MIGRATION: every category the class actually
 * uses has to be told where it now belongs. This module is the pure half of
 * that — no React, no Firestore — so the rules can be tested on their own.
 */

/** Categories the class's assessments actually reference, in first-seen order. */
export function usedCategoryIds(assessments: readonly Assessment[] | undefined): string[] {
  const seen: string[] = [];
  for (const a of assessments || []) {
    if (a.categoryId && !seen.includes(a.categoryId)) seen.push(a.categoryId);
  }
  return seen;
}

/**
 * A starting mapping, so the teacher confirms rather than fills in a blank form.
 *
 * Single-category target: everything goes to it, and there is nothing to ask —
 * "average everything" has exactly one place for work to land.
 * Otherwise: match on category NAME first (Knowledge → Knowledge across two
 * Ontario frameworks is not a coincidence), then fall back to the first
 * category so no assessment is ever left pointing at nothing.
 */
export function autoMapping(
  from: readonly MarkSetCategory[],
  to: readonly MarkSetCategory[],
  used: readonly string[],
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!to.length) return out;
  const byName = new Map(to.map(c => [c.name.trim().toLowerCase(), c.id]));
  for (const id of used) {
    if (to.length === 1) { out[id] = to[0].id; continue; }
    const name = from.find(c => c.id === id)?.name.trim().toLowerCase();
    out[id] = (name && byName.get(name)) || to[0].id;
  }
  return out;
}

/** Every used category has somewhere valid to go. */
export function mappingComplete(
  used: readonly string[],
  mapping: Record<string, string>,
  to: readonly MarkSetCategory[],
): boolean {
  const ids = new Set(to.map(c => c.id));
  return used.every(id => !!mapping[id] && ids.has(mapping[id]));
}

/**
 * Rewrite the assessments onto the new framework.
 *
 * `categoryWeights` is keyed BY CATEGORY ID, so its keys have to move with the
 * assessment. Leaving them behind stranded a per-category weight under an id
 * the new mark set has never heard of — the weight silently reverted to 1 and
 * a test worth double quietly counted single.
 *
 * Two old categories can map to one new one; their weights are summed rather
 * than one overwriting the other, because both were real weight on real work.
 */
export function remapAssessments(
  assessments: readonly Assessment[] | undefined,
  mapping: Record<string, string>,
): Assessment[] {
  return (assessments || []).map(a => {
    const nextId = a.categoryId ? mapping[a.categoryId] : undefined;
    if (!nextId || nextId === a.categoryId) return a;

    let categoryWeights = a.categoryWeights;
    if (categoryWeights) {
      const moved: Record<string, number> = {};
      for (const [oldId, weight] of Object.entries(categoryWeights)) {
        const target = mapping[oldId] || oldId;
        moved[target] = (moved[target] || 0) + weight;
      }
      categoryWeights = moved;
    }
    return { ...a, categoryId: nextId, ...(categoryWeights ? { categoryWeights } : {}) };
  });
}

/** True when changing to this mark set needs the teacher to decide anything. */
export function needsMappingChoice(
  used: readonly string[],
  target: Pick<MarkSet, 'categories'> | undefined,
): boolean {
  if (!used.length) return false;              // nothing to move
  if (!target?.categories?.length) return false;
  return target.categories.length > 1;         // one target = no decision to make
}

/**
 * The teacher's existing "average everything" mark set, if they have one.
 *
 * ONE predicate, shared by the picker and the resolver, because they have to
 * agree on WHICH set the sentinel means. They did not: the settings dialog
 * built its category mapping from the literal `AVERAGE_ALL_MARK_SET` (category
 * id `'overall'`) while the resolver returned whatever single-category set the
 * teacher already owned — and every set made through the Mark Sets picker has
 * regenerated ids like `cat_1712…`. So the mapping pointed every assessment at
 * a category the chosen set did not have, `calculateAverage` matched nothing,
 * and every student's average silently became null. Exactly the failure the
 * migration exists to prevent.
 *
 * Matched on SHAPE, not name or id: a teacher may have renamed theirs.
 */
export function findAverageAllMarkSet<T extends { categories?: MarkSetCategory[] }>(
  markSets: readonly T[] | undefined,
): T | undefined {
  return (markSets || []).find(
    m => m.categories?.length === 1 && m.categories[0]?.weight === 100,
  );
}
